import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import HamburgerNav from './HamburgerNav';
import './OriginStory.css';

const OriginStory = () => {
  const [currentPolaroid, setCurrentPolaroid] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedPolaroid, setExpandedPolaroid] = useState(null);

  // All polaroids data (including main one)
  const allPolaroids = [
    {
      id: 0,
      image: '/images/matthemenway.webp',
      caption: 'Matt Hemenway En Route to Paris',
      isMain: true
    },
    {
      id: 1,
      image: '/images/MatthewBoardsAPlane.webp',
      caption: 'This is me about to fly across the Ocean'
    },
    {
      id: 2,
      image: '/images/JessNandos.webp',
      caption: 'Jess Tries Peri Peri'
    },
    {
      id: 3,
      image: '/images/JoyceTakesaBigBite.webp',
      caption: 'Joyce Takes a Bite'
    },
    {
      id: 4,
      image: '/images/footyinthepark.webp',
      caption: 'Cezar and Paolo know ball'
    },
    {
      id: 5,
      image: '/images/boys.webp',
      caption: 'The Boys Really Outside'
    },
    {
      id: 6,
      image: '/images/hosteldinner.webp',
      caption: 'Finest Dining in London'
    }
  ];

  // Side polaroids for desktop
  const sidePolaroids = allPolaroids.slice(1).map((polaroid, index) => ({
    ...polaroid,
    className: `polaroid-${index + 1}`
  }));

  useEffect(() => {
    // Check if mobile on mount and resize
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1100);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Intersection Observer for side polaroids (desktop only)
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    // Only observe if not mobile
    if (!isMobile) {
      const polaroids = document.querySelectorAll('.side-polaroid');
      polaroids.forEach(el => {
        observer.observe(el);
      });
    }

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkMobile);
      const polaroids = document.querySelectorAll('.side-polaroid');
      polaroids.forEach(el => {
        observer.unobserve(el);
      });
    };
  }, [isMobile]);

  // Handle polaroid click
  const handlePolaroidClick = (polaroidData) => {
    setExpandedPolaroid(polaroidData);
  };

  // Handle overlay click
  const handleOverlayClick = () => {
    setExpandedPolaroid(null);
  };

  // Carousel navigation
  const nextPolaroid = () => {
    setCurrentPolaroid((prev) => (prev + 1) % allPolaroids.length);
  };

  const prevPolaroid = () => {
    setCurrentPolaroid((prev) => (prev - 1 + allPolaroids.length) % allPolaroids.length);
  };

  // Touch handling for swipe
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextPolaroid();
    } else if (isRightSwipe) {
      prevPolaroid();
    }
  };

  return (
    <div 
      className="origin-story-container"
      style={{
        backgroundImage: `url('/images/BackPassBackgroundGuy.webp')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Hamburger Navigation */}
      <HamburgerNav />

      {/* Popup overlay for expanded polaroids */}
      {expandedPolaroid && (
        <div className={`polaroid-popup-overlay ${expandedPolaroid ? 'active' : ''}`} onClick={handleOverlayClick}>
          <div className="polaroid-popup" onClick={(e) => e.stopPropagation()}>
            <div className="polaroid-popup-content">
              <img 
                src={expandedPolaroid.image} 
                alt={expandedPolaroid.caption} 
                className="polaroid-popup-image" 
              />
              <div className="polaroid-popup-caption">
                {expandedPolaroid.caption}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Container */}
      <div className="main-container">
        {/* Logo Header */}
        <Link to="/" className="logo-header">
          <img src="/images/logodownsized.webp" alt="BackPass Logo" className="logo" />
        </Link>

        {/* Origin Story Title */}
        <h1 className="origin-story-title">Origin Story</h1>

        {/* Centered Polaroid - becomes carousel on mobile */}
        <div 
          className="polaroid-container"
          onTouchStart={isMobile ? onTouchStart : undefined}
          onTouchMove={isMobile ? onTouchMove : undefined}
          onTouchEnd={isMobile ? onTouchEnd : undefined}
        >
          <div 
            className="polaroid-large clickable-polaroid" 
            onClick={() => handlePolaroidClick(isMobile ? allPolaroids[currentPolaroid] : allPolaroids[0])}
          >
            <div className="polaroid-inner">
              <img 
                src={isMobile ? allPolaroids[currentPolaroid].image : allPolaroids[0].image} 
                alt={isMobile ? allPolaroids[currentPolaroid].caption : allPolaroids[0].caption} 
                className="polaroid-image-large" 
              />
              <div className="polaroid-caption">
                {isMobile ? allPolaroids[currentPolaroid].caption : allPolaroids[0].caption}
              </div>
            </div>
          </div>

          {/* Mobile navigation */}
          {isMobile && (
            <div className="mobile-nav-container">
              <button className="carousel-nav-main carousel-nav-prev-main" onClick={prevPolaroid}>
                ‹
              </button>
              <button className="carousel-nav-main carousel-nav-next-main" onClick={nextPolaroid}>
                ›
              </button>
            </div>
          )}
        </div>

        {/* Story Content with Side Polaroids */}
        <div className="story-section">
          {/* Desktop Side Polaroids */}
          {!isMobile && sidePolaroids.map((polaroid) => (
            <div 
              key={polaroid.id} 
              className={`side-polaroid ${polaroid.className} clickable-polaroid`}
              onClick={() => handlePolaroidClick(polaroid)}
            >
              <div className="polaroid-inner">
                <img src={polaroid.image} alt={polaroid.caption} className="side-polaroid-image" />
                <div className="side-polaroid-caption">{polaroid.caption}</div>
              </div>
            </div>
          ))}

          {/* Story Content */}
          <div className="story-content">
            <p className="story-text">
              My name is Matthew Griffin and in November 2021 I was on my first backpacking trip. Like many before they travel, I was terrified of being overwhelmed. A devastating heartbreak months prior had given me incentive to obsessively plan and map my journey, and, like most plans, it flew out the window almost as soon as I got off of the plane. My original plan had been to go from Amsterdam, to London, to Edinburgh. (I was painfully reliant on my English) and I had bought a pass that allowed me flexible transportation because it made sense financially. However, from my accommodation, no such pass existed. This meant I had booked some hostels weeks in advance. This would come back to haunt me.
            </p>

            <p className="story-text">
              Amsterdam had been a rush, and when I arrived in London, I was already exhausted. I stayed at a hostel called "Prime Backpackers" in the neighborhood of Angel, a whimsical little venue nestled near the train station at Kings Cross. I had been feeling exhausted, and I remember my first night there. I checked in, fell asleep, and woke up 14 hours later. When I went downstairs I found myself talking to a tall guy with a scraggly beard who shared a name with me. His name was Matt Hemenway, and for the next week or so, the two of us shared London.
            </p>

            <p className="story-text">
              I had originally convinced him to travel north to Edinburgh, but on our last night, Matt met a woman who was going to Paris. The only thing standing in our way was my premature room reservation. I made the call and cancelled my reservation. When they wouldn't refund me, I was shocked that my transportation pass had no accommodation equivalent. This gap in the experience was the earliest seedlings of BackPass.
            </p>

            <p className="story-text">
              In Paris we ate strange food, snuck into grand cathedrals, and joined a rave in the catacombs upon the invitation of a drunk waiter. We met a majestic white-haired Scottish woman named Glenda who danced with us both until the sun rose. For about a week we met excited people from across the world, caught trains with seconds to spare, and watched the sun rise from rooftops. Matt eventually caught up to his date, and I had more room reservations on the other side of the continent I was too frugal to miss, but I left Paris with my heart full, because thanks to Matt, not only did I have a business idea, I had made a friend that knew me at my best.
            </p>

            <p className="story-text">
              Years passed as they tend to do, and it took a minute for me to grow into the person capable of being the who I needed to be to make BackPass happen. Through a mosaic of beautiful stories and souls that span this globe, you've found your way here. I wholeheartedly welcome you to BackPass.
            </p>
          </div>
        </div>

        {/* Push Me Button */}
        <div className="push-button-container">
          <Link to="/go" className="push-button">Push Me</Link>
        </div>
      </div>
    </div>
  );
};

export default OriginStory;